import { useEffect, useRef, useState } from "react";
import { fmtPhone } from "../lib/format.js";
import { downloadBlob, whatsappLink } from "../lib/print.js";
import { errorMessage } from "../lib/useQuery.js";
import { Alert, Button, Sheet } from "./ui.js";

/**
 * Two-step WhatsApp hand-off. A web page cannot attach a file to a wa.me link, so:
 *   1. "Abrir chat" opens wa.me with a text that announces the PDF (works for numbers that are
 *      not in the lab's contacts, and leaves the chat first in WhatsApp's recent list).
 *   2. "Enviar PDF" hands the file to the OS share sheet (Web Share Level 2: Android Chrome,
 *      Windows Chrome/Edge) so the chemist picks WhatsApp → the patient's chat. Without file
 *      sharing, or if the share fails, the PDF is downloaded with drag-and-drop instructions.
 * `onDelivered` runs once per opening of the sheet, when the PDF step completes.
 */
export function whatsappMessage(firstName: string, folio: string): string {
  return `Hola ${firstName}, sus resultados del laboratorio (folio ${folio}) ya están listos. En seguida le enviamos el PDF. Cualquier duda, con gusto le atendemos.`;
}

export type PdfShareOutcome = "shared" | "cancelled" | "unsupported";

/** Share files through the OS share sheet (Web Share Level 2). `unsupported` also covers a share that failed. */
export async function shareFiles(files: File[], title: string): Promise<PdfShareOutcome> {
  if (typeof navigator.share !== "function" || !navigator.canShare?.({ files })) return "unsupported";
  try {
    await navigator.share({ files, title });
    return "shared";
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") return "cancelled";
    return "unsupported";
  }
}

/** Share a PDF through the OS share sheet. `unsupported` also covers a share that failed. */
export function sharePdf(blob: Blob, filename: string, title: string): Promise<PdfShareOutcome> {
  return shareFiles([new File([blob], filename, { type: "application/pdf" })], title);
}

export function WhatsAppSendSheet({
  open,
  onClose,
  firstName,
  phone,
  folio,
  pdf,
  filename,
  onDelivered,
}: {
  open: boolean;
  onClose: () => void;
  firstName: string;
  phone: string;
  folio: string;
  pdf: Blob;
  filename: string;
  onDelivered: () => Promise<void>;
}) {
  const [chatOpened, setChatOpened] = useState(false);
  const [pdfDone, setPdfDone] = useState<"shared" | "downloaded" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recorded = useRef(false);

  useEffect(() => {
    if (!open) return;
    setChatOpened(false);
    setPdfDone(null);
    setError(null);
    recorded.current = false;
  }, [open]);

  async function markDelivered() {
    if (recorded.current) return;
    recorded.current = true;
    try {
      await onDelivered();
    } catch (e) {
      recorded.current = false;
      setError(errorMessage(e));
    }
  }

  function openChat() {
    window.open(whatsappLink(phone, whatsappMessage(firstName, folio)), "_blank", "noopener");
    setChatOpened(true);
  }

  function download() {
    downloadBlob(pdf, filename);
    setPdfDone("downloaded");
    return markDelivered();
  }

  async function sendPdf() {
    setBusy(true);
    setError(null);
    try {
      // The blob is already in memory, so share() runs inside the tap's user activation.
      const outcome = await sharePdf(pdf, filename, `Resultados ${folio}`);
      if (outcome === "cancelled") return;
      if (outcome === "shared") {
        setPdfDone("shared");
        await markDelivered();
      } else {
        await download();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Enviar por WhatsApp"
      footer={
        <Button variant={pdfDone ? "primary" : "ghost"} onClick={onClose}>
          {pdfDone ? "Listo" : "Cerrar"}
        </Button>
      }
    >
      <p className="muted">
        Para {firstName} · <strong>{fmtPhone(phone)}</strong> · <span className="small">{filename}</span>
      </p>

      <div className="stack">
        <div className="section-title" style={{ marginBottom: 0 }}>
          <span className="step">1</span>
          <span className="grow">
            <strong>Abre el chat del paciente</strong>
            <div className="small muted">Se abre WhatsApp con un mensaje que avisa que en seguida le llega el PDF. Envía ese mensaje.</div>
          </span>
        </div>
        <Button variant={chatOpened ? "secondary" : "primary"} size="lg" block icon={chatOpened ? "check" : "whatsapp"} onClick={openChat}>
          {chatOpened ? "Chat abierto · abrir de nuevo" : "Abrir chat"}
        </Button>
      </div>

      <div className="stack">
        <div className="section-title" style={{ marginBottom: 0 }}>
          <span className="step">2</span>
          <span className="grow">
            <strong>Envía el PDF</strong>
            <div className="small muted">Elige WhatsApp y el chat del paciente, que ya aparece primero en la lista.</div>
          </span>
        </div>
        <Button variant={chatOpened && !pdfDone ? "primary" : "secondary"} size="lg" block icon={pdfDone ? "check" : "send"} busy={busy} onClick={() => void sendPdf()}>
          {pdfDone === "shared" ? "PDF enviado · enviar de nuevo" : "Enviar PDF"}
        </Button>
        {pdfDone === "downloaded" ? (
          <Alert tone="blue">
            Se descargó <strong>{filename}</strong>. Arrastra el archivo <strong>{filename}</strong> al chat de WhatsApp, o toca el clip → Documento.
          </Alert>
        ) : (
          <Button variant="ghost" size="sm" icon="download" onClick={() => void download()}>
            ¿No aparece WhatsApp? Descargar el PDF
          </Button>
        )}
      </div>

      {error && <Alert tone="red">{error}</Alert>}
    </Sheet>
  );
}
